import { useLazyQuery } from '@apollo/client';
import { GET_USER_ONBOARDING_STEPS } from '../UserOnboardingDialog.gql';
import { useStartIdentityVerification } from '../../IdentityVerificationDialog/IdentityVerificationDialogHooks';
import { setElectronicSignatureCreationDialogVar } from '../../ElectronicSignatureCreationDialog/ElectronicSignatureCreationDialog.vars';
import { useStartSigningOnboardingDocuments } from '../../SignOnboardingDocumentsDialog/SignOnboardingDocumentsHooks';
import { setCompletePersonalInformationDialogVar } from '../../CompletePersonalInformationDialog/CompletePersonalInformationDialog.vars';
import { isVerificationTokenPresent } from '../../IdentityVerificationDialog/IdentityVerificationDialog.helpers';
import type {
  AdvanceNextDialogProps,
  EmployeeRecordDialogFunction,
  HandleCompleteEmploymentRecordProps,
  OnboardingStep,
  OnboardingSubStep,
} from '../UserOnboardingDialog.types';
import { handleStartVideoAgreementIdentityVerification } from '../../VideoAgreementIdentityVerificationDialog/VideoAgreementIdentityVerificationDialog.helpers';
import {
  setRequestedEmployeeDocumentsDialogVar,
  resetRequestedEmployeeDocumentsDialog,
} from '../../RequestedEmployeeDocumentsDialog/RequestedEmployeeDocumentsDialog.vars';
import {
  IDENTITY_VERIFICATION_SUB_STEPS,
  CREATE_ELECTRONIC_SIGNATURE_SUB_STEPS,
  ONBOARDING_DOCUMENTS_DESCRIPTIONS,
  IDENTITY_VERIFICATION_LABELS,
} from '../UserOnboardingDialog.constants';
import {
  setUserOnboardingDialogVar,
  userOnboardingDialogVar,
} from '../UserOnboardingDialog.vars';
import {
  globalBackdropVar,
  globalSnackbarVar,
} from '../../../../cache.reactiveVars';
import {
  resetCompleteOnboardingBeneficiariesDialog,
  setCompleteOnboardingBeneficiariesDialogVar,
} from '../../CompleteOnboardingBeneficiariesDialog/CompleteOnboardingBeneficiariesDialog.vars';
import {
  GetUserOnboardingStepsResult,
  VerificationTypeEnum,
} from '../../../../__generated__/graphql';

export function useStartUserOnboarding() {
  const [getUserOnboardingSteps] = useLazyQuery(GET_USER_ONBOARDING_STEPS);
  const { handleStartIdentityVerification } = useStartIdentityVerification();
  const { handleStartSigningOnboardingDocuments } =
    useStartSigningOnboardingDocuments();

  const updateCurrentStep = () => {
    const { steps, currentStep } = userOnboardingDialogVar();
    // check if new step is completed and advance to next step until a non completed step is found
    let newStep = currentStep + 1;
    while (newStep < steps.length && steps[newStep].completed) {
      newStep++;
    }
    setUserOnboardingDialogVar({ currentStep: newStep });
  };

  const hideCurrentDialog = () => {
    setUserOnboardingDialogVar({ open: false });
  };

  const showCurrentDialog = () => {
    setUserOnboardingDialogVar({ open: true });
  };

  const advanceNextDialogWhenOnboardingIsCompleted = (props) => {
    const { indexOnboardingComplete, steps = [] } = props;
    if (indexOnboardingComplete < steps.length) {
      steps[indexOnboardingComplete]();
    } else {
      setUserOnboardingDialogVar({ onboardingCompleted: true });
    }
  };

  const advanceNextDialog = (props: AdvanceNextDialogProps) => {
    const { indexDialog, steps = [], stepNumberLabel } = props;
    if (indexDialog < steps.length) {
      steps[indexDialog](stepNumberLabel);
    } else {
      showCurrentDialog();
      updateCurrentStep();
    }
  };

  const handleCompleteEmploymentRecord = (
    props: HandleCompleteEmploymentRecordProps,
  ) => {
    const { indexDialog, steps = [], stepNumberLabel } = props;
    if (steps.length > 0) {
      steps[indexDialog](stepNumberLabel);
    }
  };

  const handleStartUserOnboarding = async () => {
    try {
      globalBackdropVar({
        open: true,
        clickable: false,
        text: 'Verificando registro...',
      });

      const { data } = await getUserOnboardingSteps();
      const onboardingStepsData =
        data.getUserOnboardingSteps as GetUserOnboardingStepsResult;

      if (
        onboardingStepsData.__typename ===
        'CurpRequiredForVideoAgreementRegistrationType'
      ) {
        setUserOnboardingDialogVar({
          open: true,
          showWelcome: true,
          errorStartingOnboarding: true,
        });
        globalSnackbarVar({
          show: true,
          message: 'CURP requerido para completar el registro',
          severity: 'error',
        });
        return;
      }

      if (onboardingStepsData.__typename !== 'UserOnboardingSteps') {
        setUserOnboardingDialogVar({
          open: true,
          showWelcome: true,
          errorStartingOnboarding: true,
        });
        globalSnackbarVar({
          show: true,
          message: 'Ocurrió un error, contacte a Sora',
          severity: 'error',
        });
        return;
      }

      const {
        verifyIdentity,
        onboardingStatus,
        createElectronicSignature,
        signOnboardingDocuments,
        uploadRequestedDocuments,
        completeRequestedInformation,
        completeBeneficiaries,
      } = onboardingStepsData;

      const { isVisible, isCompleted } = onboardingStatus;

      if (!isVisible || isCompleted) {
        const ONBOARDING_COMPLETE_DIALOGS: Function[] = [];
        let indexOnboardingComplete = 0;

        // requested information is required even if the onboarding is completed
        if (!completeRequestedInformation.completed) {
          ONBOARDING_COMPLETE_DIALOGS.push(() =>
            setCompletePersonalInformationDialogVar({
              open: true,
              showLogoutDialogAtClose: true,
              onFinish: () => {
                indexOnboardingComplete++;
                advanceNextDialogWhenOnboardingIsCompleted({
                  indexOnboardingComplete,
                  steps: ONBOARDING_COMPLETE_DIALOGS,
                });
              },
            }),
          );
        }

        //Beneficiaries is required even if the onboarding is completed
        if (!completeBeneficiaries.completed) {
          ONBOARDING_COMPLETE_DIALOGS.push(() =>
            setCompleteOnboardingBeneficiariesDialogVar({
              open: true,
              showLogoutDialogAtClose: true,
              onFinish: () => {
                indexOnboardingComplete++;
                advanceNextDialogWhenOnboardingIsCompleted({
                  indexOnboardingComplete,
                  steps: ONBOARDING_COMPLETE_DIALOGS,
                });
              },
            }),
          );
        }

        if (ONBOARDING_COMPLETE_DIALOGS.length > 0) {
          setUserOnboardingDialogVar({ onboardingCompleted: false });
          ONBOARDING_COMPLETE_DIALOGS[indexOnboardingComplete]();
        } else {
          setUserOnboardingDialogVar({ onboardingCompleted: true });
        }

        return;
      }

      let currentStep = -1;
      let onboardingSteps: OnboardingStep[] = [];

      /**
       * add identity verification step to onboarding steps if identity verification
       * is required for electronic signature creation,  electronic signature is not
       * completed and verification token is not present in user session
       */
      const { verificationTypeRequired } = createElectronicSignature;
      const { videoAgreementTextToRead } = verifyIdentity;

      const identityVerificationLabel =
        IDENTITY_VERIFICATION_LABELS[verificationTypeRequired!] ||
        'Verifica tu identidad';
      const identityVerificationSubSteps =
        IDENTITY_VERIFICATION_SUB_STEPS[verificationTypeRequired!] || [];
      const isOnboardingWithVideoAgreement =
        verificationTypeRequired ===
        VerificationTypeEnum.LivenessVideoAgreement;

      const identityVerificationCompleted =
        !createElectronicSignature.verificationTokenRequired ||
        (verifyIdentity.completed! && isVerificationTokenPresent()) ||
        (createElectronicSignature.completed && verifyIdentity.completed);

      onboardingSteps.push({
        type: 'IDENTITY_VERIFICATION',
        label: identityVerificationLabel,
        subLabel: 'Necesitas:',
        subSteps: identityVerificationSubSteps,
        actionLabel: 'Verificar identidad',
        animation: isOnboardingWithVideoAgreement
          ? 'videoAgreementIdentityVerification'
          : 'identityVerification',
        completed: identityVerificationCompleted,
        action: () => {},
      });
      if (!identityVerificationCompleted) {
        const stepIndex = onboardingSteps.length - 1;
        onboardingSteps[stepIndex].action = (stepNumberLabel) => {
          if (isOnboardingWithVideoAgreement) {
            handleStartVideoAgreementIdentityVerification({
              stepNumberLabel,
              videoAgreementTextToRead,
              onClose: showCurrentDialog,
              onStarted: hideCurrentDialog,
              onFinish: () => {
                showCurrentDialog();
                updateCurrentStep();
              },
            });
          } else {
            handleStartIdentityVerification({
              stepNumberLabel,
              onClose: showCurrentDialog,
              onStarted: hideCurrentDialog,
              onFinish: () => {
                showCurrentDialog();
                updateCurrentStep();
              },
            });
          }
        };
        if (currentStep === -1) currentStep = stepIndex;
      }

      // add electronic signature creation step and check if completed
      const signatureCreationCompleted = createElectronicSignature.completed;
      onboardingSteps.push({
        type: 'CREATE_ELECTRONIC_SIGNATURE',
        label: 'Crear tu NIP de firma electrónica',
        subLabel: 'Necesitas:',
        subSteps: CREATE_ELECTRONIC_SIGNATURE_SUB_STEPS,
        actionLabel: 'Crea tu NIP de firma electrónica',
        animation: 'createElectronicSignature',
        completed: signatureCreationCompleted,
        action: () => {},
      });
      if (!signatureCreationCompleted) {
        const stepIndex = onboardingSteps.length - 1;
        onboardingSteps[stepIndex].action = (stepNumberLabel) => {
          hideCurrentDialog();
          setElectronicSignatureCreationDialogVar({
            open: true,
            stepNumberLabel,
            onClose: showCurrentDialog,
            onCreationSuccess: () => {
              showCurrentDialog();
              updateCurrentStep();
            },
          });
        };
        if (currentStep === -1) currentStep = stepIndex;
      }

      // add onboarding documents signing step and check if completed
      const { pendingDocuments } = signOnboardingDocuments;
      const allOnboardingDocsSigned = pendingDocuments.length === 0;
      const SIGN_ONBOARDING_DOCUMENTS_SUB_STEPS = pendingDocuments.map(
        (doc) => {
          return {
            label: doc.title,
            description: ONBOARDING_DOCUMENTS_DESCRIPTIONS[doc.constantType],
          };
        },
      );
      onboardingSteps.push({
        type: 'SIGN_ONBOARDING_DOCUMENTS',
        label: 'Consentimiento de uso de sora',
        subLabel: 'Debes leer y firmar los siguientes documentos:',
        subSteps: SIGN_ONBOARDING_DOCUMENTS_SUB_STEPS,
        actionLabel: 'Firmar documentos',
        animation: 'signOnboardingDocuments',
        completed: allOnboardingDocsSigned,
        action: () => {},
      });
      if (!allOnboardingDocsSigned) {
        const stepIndex = onboardingSteps.length - 1;
        onboardingSteps[stepIndex].action = () => {
          handleStartSigningOnboardingDocuments({
            onClose: showCurrentDialog,
            onStarted: hideCurrentDialog,
            onFinish: () => {
              showCurrentDialog();
              updateCurrentStep();
            },
          });
        };
        if (currentStep === -1) currentStep = stepIndex;
      }

      // add step to complete employment record
      if (
        !uploadRequestedDocuments.completed ||
        !completeRequestedInformation.completed ||
        !completeBeneficiaries.completed
      ) {
        let indexDialog = 0;
        const EMPLOYMENT_RECORDS_DIALOGS: EmployeeRecordDialogFunction[] = [];
        const COMPLETE_EMPLOYMENT_RECORD_SUB_STEPS: OnboardingSubStep[] = [];

        // complete employment record
        if (!uploadRequestedDocuments.completed) {
          COMPLETE_EMPLOYMENT_RECORD_SUB_STEPS.push({
            label: 'Cargar documentos para completar tu expediente',
            description: '',
            key: 'UPLOAD_REQUESTED_DOCUMENTS',
          });

          EMPLOYMENT_RECORDS_DIALOGS.push((stepNumberLabel) => {
            hideCurrentDialog();
            setRequestedEmployeeDocumentsDialogVar({
              open: true,
              stepNumberLabel,
              onClose: showCurrentDialog,
              onFinish: () => {
                resetRequestedEmployeeDocumentsDialog();
                indexDialog++;
                advanceNextDialog({
                  indexDialog,
                  steps: EMPLOYMENT_RECORDS_DIALOGS,
                  stepNumberLabel,
                });
              },
            });
          });
        }

        if (!completeRequestedInformation.completed) {
          COMPLETE_EMPLOYMENT_RECORD_SUB_STEPS.push({
            label: 'Completar tu información personal',
            description: '',
            key: 'COMPLETE_REQUESTED_INFORMATION',
          });

          EMPLOYMENT_RECORDS_DIALOGS.push((stepNumberLabel) => {
            hideCurrentDialog();
            setCompletePersonalInformationDialogVar({
              open: true,
              stepNumberLabel,
              onClose: showCurrentDialog,
              onFinish: () => {
                indexDialog++;
                advanceNextDialog({
                  indexDialog,
                  steps: EMPLOYMENT_RECORDS_DIALOGS,
                  stepNumberLabel,
                });
              },
            });
          });
        }

        if (!completeBeneficiaries.completed) {
          COMPLETE_EMPLOYMENT_RECORD_SUB_STEPS.push({
            label: 'Completar información de beneficiarios',
            description: '',
            key: 'COMPLETE_BENEFICIARIES',
          });

          EMPLOYMENT_RECORDS_DIALOGS.push((stepNumberLabel) => {
            hideCurrentDialog();
            setCompleteOnboardingBeneficiariesDialogVar({
              open: true,
              stepNumberLabel,
              onClose: showCurrentDialog,
              onFinish: () => {
                indexDialog++;
                resetCompleteOnboardingBeneficiariesDialog();
                advanceNextDialog({
                  indexDialog,
                  steps: EMPLOYMENT_RECORDS_DIALOGS,
                  stepNumberLabel,
                });
              },
            });
          });
        }

        onboardingSteps.push({
          type: 'COMPLETE_EMPLOYMENT_RECORD',
          label: 'Completa tu expediente',
          subLabel: 'Necesitas:',
          subSteps: COMPLETE_EMPLOYMENT_RECORD_SUB_STEPS,
          actionLabel: 'Completar expediente',
          animation: 'completeEmploymentRecord',
          completed: false,
          action: (stepNumberLabel) => {
            handleCompleteEmploymentRecord({
              indexDialog,
              steps: EMPLOYMENT_RECORDS_DIALOGS,
              stepNumberLabel,
            });
          },
        });
        if (currentStep === -1) currentStep = onboardingSteps.length - 1;
      }

      const allStepsCompleted = onboardingSteps.every((step) => step.completed);
      if (allStepsCompleted) currentStep = onboardingSteps.length;

      setUserOnboardingDialogVar({
        open: true,
        steps: onboardingSteps,
        showWelcome: !allStepsCompleted,
        onboardingCompleted: false,
        currentStep,
      });
    } catch {
      setUserOnboardingDialogVar({
        open: true,
        showWelcome: true,
        errorStartingOnboarding: true,
      });
      globalSnackbarVar({
        show: true,
        message: 'Ocurrió un error, contacte a Sora',
        severity: 'error',
      });
    } finally {
      globalBackdropVar({ open: false });
    }
  };

  return { handleStartUserOnboarding };
}
